import '@/assets/styles/main.scss';
import { glsClearLocalStorageWhenAppVersionChange } from '@gls-its-frontends/common/gls-app-config';
import { glsVueQueryPluginOptions } from '@gls-its-frontends/common/gls-query';
import { glsCreateI18nInstance } from '@gls-its-frontends/common/gls-translate';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia, Pinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { type App as AppType, createApp, type Plugin } from 'vue';
import de from '@/assets/i18n/de.json';
import en from '@/assets/i18n/en.json';
import fr from '@/assets/i18n/fr.json';
import pl from '@/assets/i18n/pl.json';
import { LOCAL_STORAGE_PREFIX } from '@/constants/local-storage-prefix';
import { PermissionDirective } from '@/directives/v-permission';
import { Environment } from '@/enums/environment';
import { getEnvironment } from '@/helpers/get-environment';
import { setAppTitle } from '@/helpers/set-app-title';
import { setUpDynatrace } from '@/helpers/set-up-dynatrace';
import { setUpEnvironmentConfig } from '@/helpers/set-up-environment-config';
import { appProviderPlugin } from '@/plugins/app-provider-plugin';
import { FeatureFlagService } from '@/services/feature-flag-service';

(async (): Promise<void> => {
    const environment: Environment = getEnvironment();

    await FeatureFlagService.init();
    glsClearLocalStorageWhenAppVersionChange(APP_VERSION, LOCAL_STORAGE_PREFIX);

    setUpEnvironmentConfig(environment);
    setUpDynatrace(environment);
    setAppTitle(environment);

    const { default: App } = await import('@/App.vue');
    const { default: router } = await import('@/routing/router');

    const app: AppType<Element> = createApp(App);

    const pinia: Pinia = createPinia().use(piniaPluginPersistedstate);
    const i18n: Plugin = glsCreateI18nInstance({ de, en, pl, fr });

    app.directive('permission', PermissionDirective)
        .use(appProviderPlugin)
        .use(pinia)
        .use(i18n)
        .use(VueQueryPlugin, glsVueQueryPluginOptions)
        .use(router)
        .mount('#root');
})();
